/**
 * Wearth "Filter & sort" panel on collection pages. Opens the panel as a modal dialog (side drawer on
 * desktop, bottom sheet on mobile); "Show results" submits a plain GET form, so the collection reloads
 * with Shopify's own filter and sort parameters. Also:
 * - a selected price option can be tapped again to clear it;
 * - sizes stored under several names ("S" and "Small") are submitted together;
 * - empty fields are left out so the URL stays clean.
 */
class WearthFilters extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const panel = this.panel;
    const form = this.querySelector('[data-form]');

    this.querySelector('[data-open]')?.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-close]')?.addEventListener('click', () => panel?.close(), { signal });

    // A click on the backdrop lands on the dialog itself
    panel?.addEventListener(
      'click',
      (event) => {
        if (event.target === panel) panel.close();
      },
      { signal }
    );
    panel?.addEventListener('close', () => this.querySelector('[data-open]')?.focus(), { signal });

    for (const radio of this.querySelectorAll('input[data-deselectable]')) {
      const input = /** @type {HTMLInputElement} */ (radio);
      input.addEventListener('pointerdown', () => (input.dataset.wasChecked = String(input.checked)), { signal });
      input.addEventListener(
        'click',
        () => {
          if (input.dataset.wasChecked === 'true') input.checked = false;
          delete input.dataset.wasChecked;
        },
        { signal }
      );
    }

    form?.addEventListener('submit', this.#handleSubmit, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLDialogElement | null} */
  get panel() {
    return this.querySelector('dialog[data-panel]');
  }

  open() {
    const panel = this.panel;
    if (!panel || panel.open) return;
    panel.showModal();
  }

  #handleSubmit = (/** @type {SubmitEvent} */ event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const params = new URLSearchParams();

    for (const [name, value] of new FormData(form)) {
      if (typeof value === 'string' && value !== '') params.append(name, value);
    }

    for (const checkbox of form.querySelectorAll('input[data-extra]:checked')) {
      const extra = new URLSearchParams(/** @type {HTMLInputElement} */ (checkbox).dataset.extra);
      for (const [name, value] of extra) params.append(name, value);
    }

    const query = params.toString();
    window.location.assign(form.action.split('?')[0] + (query ? `?${query}` : ''));
  };
}

if (!customElements.get('wearth-filters')) {
  customElements.define('wearth-filters', WearthFilters);
}
